import { isNull } from 'lodash-es'
import { getRequest, postRequest, retryUntilSuccessOrFailure } from '../utils'

export const getExportDocument: RequestTypeVariable = {
  getKey: (id: string) => ['getExportDocument', id],
  request: async (
    id: string,
    documentPath: string,
    params?: Record<string, string | number | boolean>,
  ): Promise<Blob> => {
    const res = await getRequest<Blob>({
      url: `/projects/${id}/export/${documentPath}`,
      responseType: 'blob',
      params: params,
    })
    return res.data as Blob
  },
}

export const addPlanElement: RequestTypeVariable = {
  getKey: (id: string) => ['addPlanElement', id],
  request: async (
    id: string,
    documentPath: string,
    params?: Record<string, string | number | boolean>,
  ): Promise<Blob> => {
    const res = await postRequest<Blob>({
      url: `/projects/${id}/export/${documentPath}/plan`,
      params: params,
    })
    return res.data as Blob
  },
}

export const computeExportDocumentTask: RequestTypeVariable = {
  getKey: (id: string) => ['computeExportDocumentTask', id],
  request: async (
    projectId: string,
    taskPath: string,
    params?: Record<string, string | number | boolean>,
  ): Promise<void> => {
    const { data: computeDocumentData } = await postRequest<{
      task_id: string
    } | null>({
      url: `/projects/${projectId}/export/${taskPath}/compute`,
      params: params,
    })

    // TODO: is it really necessary to throw a new error here?
    if (isNull(computeDocumentData)) throw new Error('Cannot compute document yet')

    // retry for 30 minutes with 5 seconds interval
    await retryUntilSuccessOrFailure(
      {
        url: `/task-status/${computeDocumentData?.task_id}`,
      },
      12 * 30,
      5000,
    )
  },
}

export const getMultiStoreyExportDocument: RequestTypeVariable = {
  getKey: (id: string) => ['getExportDocument', id],
  request: async (
    id: string,
    documentPath: string,
    storeys: Array<string>,
    params?: Record<string, string>,
  ): Promise<Blob> => {
    // const storeysPayload = storeys.map(v => (v === 'ROOF' ? 'ROOF' : Number(v)))
    const res = await postRequest<Blob>({
      url: `/projects/${id}/export/${documentPath}`,
      responseType: 'blob',
      params: params,
      data: {
        storeys: storeys,
      },
    })
    return res.data as Blob
  },
}

export const postDownloadPositionPlanDxf: RequestTypeVariable = {
  getKey: (id: string) => ['postDownloadPositionPlanDxf', id],
  request: async (id: string, storeys: Array<string>): Promise<Blob> => {
    const res = await postRequest<Blob>({
      url: `/projects/${id}/export/position-plan/dxf`,
      responseType: 'blob',
      data: {
        storeys,
      },
    })
    return res.data as Blob
  },
}

export const postCreatePositionPlan: RequestTypeVariable = {
  getKey: (id: string) => ['postCreatePositionPlan', id],
  request: async (
    id: string,
    storeys: Array<string>,
    // URLSearchParams to repeat a key (`plan_tags=a&plan_tags=b`); axios would send `plan_tags[]`.
    params?: Record<string, string | number | boolean> | URLSearchParams,
  ): Promise<Plan[]> => {
    const { data } = await postRequest<Plan[]>({
      url: `/projects/${id}/export/position-plan/V2`,
      params: params,
      data: {
        storeys: storeys,
      },
    })
    return data as Plan[]
  },
}

export const postCreateStiffeningPlan: RequestTypeVariable = {
  getKey: (id: string) => ['postCreateStiffeningPlan', id],
  request: async (
    id: string,
    // URLSearchParams to repeat a key (`plan_tags=a&plan_tags=b`); axios would send `plan_tags[]`.
    params?: Record<string, string | number | boolean> | URLSearchParams,
  ): Promise<Plan[]> => {
    const { data } = await postRequest<Plan[]>({
      url: `/projects/${id}/export/stiffening-plan/V2`,
      params: params,
    })
    return data as Plan[]
  },
}

export const postCreateLoadPlan: RequestTypeVariable = {
  getKey: (id: string) => ['postCreateLoadPlan', id],
  request: async (
    id: string,
    // URLSearchParams to repeat a key (`plan_tags=a&plan_tags=b`); axios would send `plan_tags[]`.
    params?: Record<string, string | number | boolean> | URLSearchParams,
  ): Promise<Plan[]> => {
    const { data } = await postRequest<Plan[]>({
      url: `/projects/${id}/export/load-plan/V2`,
      params: params,
    })
    return data as Plan[]
  },
}

export const getMemberChecksDocument: RequestTypeVariable = {
  getKey: (id: string, element: string) => ['getMemberChecksDocument', id, element],
  request: async (id: string, element: string): Promise<Blob> => {
    const res = await getRequest<Blob>({
      url: `/projects/${id}/export/member-checks/${element}/document`,
      responseType: 'blob',
    })
    return res.data as Blob
  },
}

export const postPdfPlanSettings: RequestTypeVariable = {
  getKey: (id: string) => ['postPdfPlanSettings', id],
  request: async (
    id: string,
    settings?: Record<string, string | number | boolean>,
  ): Promise<PdfPlanSettings> => {
    const { data } = await postRequest<PdfPlanSettings>({
      url: `/projects/${id}/export/pdf-plan-settings`,
      data: settings,
    })
    return data as PdfPlanSettings
  },
}
